import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { 
  History, 
  Eye, 
  Target, 
  Users, 
  Building, 
  Lightbulb, 
  Shield, 
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Brain,
  Zap,
  Package,
  BarChart3,
  Handshake,
  Globe,
  Clock,
  Settings,
  Monitor,
  Layers,
  Lock,
  MessageCircle,
  Car,
  Smartphone,
  Pill,
  ShoppingCart
} from "lucide-react";

interface AboutProps {
  section?: string;
}

export function About({ section = "story" }: AboutProps) {
  const renderStory = () => (
    <div className="min-h-screen bg-background">
      {/* The Origin */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">It Started with a Challenge</h2>
            </div>

            <Card className="shadow-lg mb-8">
              <CardContent className="p-8">
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  Years of working with retail and distribution companies revealed a major gap — procurement, promotions, and vendor settlements were often misaligned, manual, and error-prone.
                </p>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  At <span className="font-semibold text-primary">Newamps Consulting Pvt. Ltd.</span>, we decided to solve this challenge—not with patches, but with a product-first, intelligence-driven approach. That's how NexProcureAI was born.
                </p>
                <blockquote className="border-l-4 border-primary pl-6 italic text-lg text-muted-foreground">
                  "We didn't want to create just another software. We wanted to build a solution that understands complexity, automates intelligently, and scales with your business."
                </blockquote>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Product Evolution Timeline */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">A Journey of Innovation</h2>
              <p className="text-lg text-muted-foreground">
                Milestones that shaped our evolution from concept to industry leader
              </p>
            </div>

            <div className="space-y-8">
              {[
                {
                  year: "2021",
                  title: "Conceptualization",
                  description: "The vision for NexProcureAI was born from identifying critical gaps in procurement automation",
                  color: "bg-primary"
                },
                {
                  year: "2022",
                  title: "First Pilot with a Large Electronics Retail Chain",
                  description: "Successfully launched our pilot program, proving the concept with real-world implementation",
                  color: "bg-secondary"
                },
                {
                  year: "2023",
                  title: "Modules: NexProcure and NexScheme Launched",
                  description: "Core platform modules went live, establishing our foundation in procurement management",
                  color: "bg-accent"
                },
                {
                  year: "2024",
                  title: "NexAuction and NexAI Added",
                  description: "Advanced AI capabilities and auction management features expanded our solution portfolio",
                  color: "bg-primary"
                },
                {
                  year: "2025",
                  title: "Platform Adopted by Top Retailers",
                  description: "Major retail enterprises across multiple industries chose NexProcureAI as their procurement partner",
                  color: "bg-secondary"
                }
              ].map((milestone, index) => (
                <div key={index} className="flex items-center gap-8">
                  <div className="flex-shrink-0">
                    <div className={`w-16 h-16 rounded-full ${milestone.color} flex items-center justify-center`}>
                      <span className="text-white font-bold text-lg">{milestone.year}</span>
                    </div>
                  </div>
                  <Card className="flex-1 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="text-xl mb-2">{milestone.title}</h3>
                      <p className="text-muted-foreground">{milestone.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why We Built NexProcureAI */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Because Complexity Demands Clarity</h2>
              <p className="text-lg text-muted-foreground">
                The key motivations that drove us to create a comprehensive solution
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: Package,
                  title: "Fragmented Processes",
                  description: "Needed unification across disparate systems and workflows"
                },
                {
                  icon: Building,
                  title: "ERP Limitations", 
                  description: "Existing systems didn't handle complex scheme management well"
                },
                {
                  icon: Clock,
                  title: "Time-Consuming Reconciliation",
                  description: "Manual reconciliation processes were eating up valuable resources"
                },
                {
                  icon: BarChart3,
                  title: "Lack of Predictive Insights",
                  description: "Decisions were reactive rather than proactive and data-driven"
                },
                {
                  icon: Globe,
                  title: "Enterprise-Ready Platform",
                  description: "Clients needed a customizable, scalable solution for complex operations"
                }
              ].map((motivation, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                        <motivation.icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-lg">{motivation.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{motivation.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F6FBEA' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Driven by Simplicity. Fueled by Intelligence.</h2>
              <p className="text-lg text-muted-foreground mb-8">
                We believe enterprise software should be:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {[
                "User-friendly yet powerful",
                "Adaptive to unique business rules", 
                "Intelligent and proactive, not just reactive",
                "Transparent, traceable, and audit-ready"
              ].map((principle, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-accent mt-0.5 flex-shrink-0" />
                      <p className="text-lg">{principle}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-white border-2 border-primary/20">
              <CardContent className="p-8 text-center">
                <blockquote className="text-xl text-muted-foreground italic mb-4">
                  "Everything we build solves a real-world pain point—and does it with clarity, control, and confidence."
                </blockquote>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Culture & People */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Built by Experts. Backed by Passion.</h2>
            </div>

            <Card className="shadow-lg">
              <CardContent className="p-8">
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  Behind NexProcureAI is a team of procurement consultants, AI engineers, product thinkers, and retail transformation specialists—united by one goal: to make P2P operations smarter and simpler.
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                  <div className="text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mx-auto mb-3">
                      <Brain className="h-8 w-8 text-primary" />
                    </div>
                    <h4 className="font-semibold">AI Engineers</h4>
                    <p className="text-sm text-muted-foreground">Cutting-edge technology</p>
                  </div>
                  <div className="text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary/10 mx-auto mb-3">
                      <Users className="h-8 w-8 text-secondary" />
                    </div>
                    <h4 className="font-semibold">Consultants</h4>
                    <p className="text-sm text-muted-foreground">Domain expertise</p>
                  </div>
                  <div className="text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/10 mx-auto mb-3">
                      <Lightbulb className="h-8 w-8 text-accent" />
                    </div>
                    <h4 className="font-semibold">Product Thinkers</h4>
                    <p className="text-sm text-muted-foreground">User-centric design</p>
                  </div>
                  <div className="text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mx-auto mb-3">
                      <TrendingUp className="h-8 w-8 text-primary" />
                    </div>
                    <h4 className="font-semibold">Specialists</h4>
                    <p className="text-sm text-muted-foreground">Retail transformation</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl text-white font-bold">
              From Vision to Execution — We're Just Getting Started
            </h2>
            <p className="mb-8 text-lg text-white/90">
              Whether you're a retail chain, distributor, or procurement-led enterprise—NexProcureAI is built to grow with you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group bg-white text-primary hover:bg-gray-100">
                <Users className="mr-2 h-4 w-4" />
                Meet Our Leadership
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" className="bg-white text-primary border-white hover:bg-gray-100">
                <Handshake className="mr-2 h-4 w-4" />
                Request a Demo
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderMission = () => (
    <div className="min-h-screen bg-background">
      {/* Core Mission Statement */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <Card className="shadow-xl border-2 border-primary/20">
              <CardContent className="p-12">
                <blockquote className="text-2xl lg:text-3xl text-primary leading-relaxed italic mb-6">
                  "To empower enterprises with intelligent, adaptive, and transparent procurement & scheme management solutions that drive measurable business outcomes."
                </blockquote>
                <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How We Deliver on Our Mission */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Making It Happen, Every Day</h2>
              <p className="text-lg text-muted-foreground">
                How we transform mission into action through our core capabilities
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Brain,
                  title: "AI-Driven Automation",
                  description: "Reduce manual effort and decision guesswork with intelligent automation that learns and adapts."
                },
                {
                  icon: Layers,
                  title: "Modular Architecture",
                  description: "Tailor-fit to complex business requirements with flexible, scalable modular components."
                },
                {
                  icon: Monitor,
                  title: "Real-Time Visibility",
                  description: "Dashboards, alerts, and predictive insights that keep you ahead of the curve."
                },
                {
                  icon: Settings,
                  title: "Enterprise Integrations",
                  description: "Seamless connection with ERP, CRM, DMS, and legacy apps for unified operations."
                },
                {
                  icon: Lock,
                  title: "Compliance & Control",
                  description: "Strong approval workflows, audit trails, and traceability for complete governance."
                },
                {
                  icon: MessageCircle,
                  title: "Customer-Centric Roadmap",
                  description: "Built with clients, for clients—our development is driven by real-world needs."
                }
              ].map((capability, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                        <capability.icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{capability.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{capability.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Purpose-Driven Solutions for Purpose-Driven Enterprises</h2>
              <p className="text-lg text-muted-foreground">
                Serving industry leaders who demand excellence in procurement and scheme management
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Smartphone,
                  title: "Electronics Retailers",
                  description: "Complex product catalogs and dynamic pricing schemes",
                  color: "bg-primary"
                },
                {
                  icon: ShoppingCart,
                  title: "FMCG Distributors",
                  description: "High-volume transactions and promotional campaign management",
                  color: "bg-secondary"
                },
                {
                  icon: Car,
                  title: "Automotive Supply Chains",
                  description: "Multi-tier supplier networks and quality compliance",
                  color: "bg-accent"
                },
                {
                  icon: Pill,
                  title: "Pharma & Healthcare",
                  description: "Regulatory compliance and traceability requirements",
                  color: "bg-primary"
                }
              ].map((industry, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow text-center">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-center mb-6">
                      <div className={`flex h-16 w-16 items-center justify-center rounded-full ${industry.color}`}>
                        <industry.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <h3 className="text-xl mb-3">{industry.title}</h3>
                    <p className="text-muted-foreground text-sm">{industry.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center p-8 bg-muted/30 rounded-2xl">
              <p className="text-lg text-muted-foreground mb-4">
                <span className="font-semibold text-primary">From startups to Fortune 500 companies</span>, our solutions scale with your ambitions and adapt to your unique business requirements.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                <div>
                  <div className="text-3xl font-bold text-primary mb-2">500+</div>
                  <p className="text-sm text-muted-foreground">Global Enterprises</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-secondary mb-2">25+</div>
                  <p className="text-sm text-muted-foreground">Countries Served</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">15+</div>
                  <p className="text-sm text-muted-foreground">Industry Verticals</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary mb-2">99.9%</div>
                  <p className="text-sm text-muted-foreground">Platform Uptime</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl text-white font-bold">
              Let's Make Procurement and Promotion Smarter — Together
            </h2>
            <p className="mb-8 text-lg text-white/90">
              Join hundreds of enterprises who have transformed their procurement operations with NexProcureAI's intelligent solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group bg-white text-primary hover:bg-gray-100">
                <Package className="mr-2 h-4 w-4" />
                Explore Our Solutions
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" className="bg-white text-primary border-white hover:bg-gray-100">
                <MessageCircle className="mr-2 h-4 w-4" />
                Talk to Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderVision = () => (
    <div className="min-h-screen bg-background">
      {/* Vision Statement */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <Card className="shadow-xl border-2 border-primary/20">
              <CardContent className="p-12">
                <blockquote className="text-2xl lg:text-3xl text-primary leading-relaxed mb-6 font-bold">
                  "To become the world's most trusted platform for intelligent procure-to-pay lifecycle management—built on AI, trust, and real-time insight."
                </blockquote>
                <div className="w-16 h-1 bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pillars of Our Vision */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">What Our Vision Is Built On</h2>
              <p className="text-lg text-muted-foreground">
                Four foundational pillars that guide everything we build and deliver
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Brain,
                  title: "Intelligence at the Core",
                  description: "AI that adapts and evolves with business needs.",
                  color: "bg-primary"
                },
                {
                  icon: Users,
                  title: "Customer Empowerment",
                  description: "Putting control and clarity in the hands of every client.",
                  color: "bg-secondary"
                },
                {
                  icon: Zap,
                  title: "Innovation Without Complexity",
                  description: "Powerful features made usable and scalable.",
                  color: "bg-accent"
                },
                {
                  icon: TrendingUp,
                  title: "Sustainable Value",
                  description: "Enabling operational excellence and long-term ROI.",
                  color: "bg-primary"
                }
              ].map((pillar, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow text-center">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-center mb-6">
                      <div className={`flex h-16 w-16 items-center justify-center rounded-full ${pillar.color}`}>
                        <pillar.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <h3 className="text-xl mb-4">{pillar.title}</h3>
                    <p className="text-muted-foreground">{pillar.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our North Star */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Guided by Impact, Not Just Innovation</h2>
              <p className="text-lg text-muted-foreground">
                The principles that drive our commitment to transformational change
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: Settings,
                  title: "Solving Deep-Rooted Business Inefficiencies",
                  description: "We tackle the fundamental challenges that have plagued procurement operations for decades, delivering solutions that address root causes, not just symptoms.",
                  color: "bg-secondary"
                },
                {
                  icon: Building,
                  title: "Building for the Enterprise, Not Just the User",
                  description: "Our solutions are architected for enterprise scale, security, and complexity while maintaining intuitive user experiences that drive adoption.",
                  color: "bg-primary"
                },
                {
                  icon: Globe,
                  title: "Scaling Across Industries and Geographies",
                  description: "From retail to manufacturing, from local markets to global enterprises, our platform adapts to diverse business environments and regulatory requirements.",
                  color: "bg-accent"
                },
                {
                  icon: Handshake,
                  title: "Partnering in Digital Transformation",
                  description: "We don't just provide software—we become strategic partners in our clients' digital transformation journeys, ensuring long-term success and continuous value creation.",
                  color: "bg-secondary"
                }
              ].map((principle, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-full ${principle.color} flex-shrink-0 mt-1`}>
                        <principle.icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-3">{principle.title}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground pl-16">{principle.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center p-8 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl">
              <h3 className="text-2xl mb-4 font-bold">Our Commitment to the Future</h3>
              <p className="text-lg text-muted-foreground mb-6 max-w-3xl mx-auto">
                Every decision we make, every feature we build, and every partnership we forge is guided by our vision of creating a world where intelligent automation empowers businesses to achieve their highest potential.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">10x</div>
                  <p className="text-sm text-muted-foreground">Efficiency Improvement Target</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-secondary mb-2">100%</div>
                  <p className="text-sm text-muted-foreground">Process Transparency Goal</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-accent mb-2">∞</div>
                  <p className="text-sm text-muted-foreground">Scalability Potential</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl text-white font-bold">
              Join Us on the Journey to Intelligent Transformation
            </h2>
            <p className="mb-8 text-lg text-white/90">
              Experience the future of procurement and scheme management today. Discover how our AI-powered platform can transform your operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group bg-white text-primary hover:bg-gray-100">
                <Brain className="mr-2 h-4 w-4" />
                Explore NexAI
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" className="bg-white text-primary border-white hover:bg-gray-100">
                <MessageCircle className="mr-2 h-4 w-4" />
                Request a Demo
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );



  switch (section) {
    case "mission":
      return renderMission();
    case "vision":
      return renderVision();
    case "story":
    default:
      return renderStory();
  }
}